import React, { useState } from 'react';
import { Spin, Alert, message } from 'antd';
import { usePWP303Query } from './hooks/usePWP303Query';
import QueryParams from './components/QueryParams';
import ResultTable from './components/ResultTable';
import type { QueryRequest } from '../../services/pwp303Api';
import './PWP303Form.css';

const PWP303Form: React.FC = () => {
  const { loading, result, queried, noData, runQuery } = usePWP303Query();

  // 5 pairs of range filters
  const [deliveryNoFrom, setDeliveryNoFrom] = useState('');
  const [deliveryNoTo, setDeliveryNoTo] = useState('');
  const [deliverySeqFrom, setDeliverySeqFrom] = useState(0);
  const [deliverySeqTo, setDeliverySeqTo] = useState(99);
  const [submitNoFrom, setSubmitNoFrom] = useState('');
  const [submitNoTo, setSubmitNoTo] = useState('');
  const [testDateFrom, setTestDateFrom] = useState('');
  const [testDateTo, setTestDateTo] = useState('');
  const [reportNoFrom, setReportNoFrom] = useState('');
  const [reportNoTo, setReportNoTo] = useState('');

  const handleQuery = () => {
    // BR-FILTER: range validation — "from" must not exceed "to"
    const dFrom = deliveryNoFrom.trim();
    const dTo = deliveryNoTo.trim();
    if (dFrom && dTo && dTo < dFrom) {
      message.warning('繳庫單號範圍不正確，結束值不可小於起始值');
      return;
    }

    if (deliverySeqFrom > deliverySeqTo) {
      message.warning('繳庫項次範圍不正確，結束值不可小於起始值');
      return;
    }

    const sFrom = submitNoFrom.trim();
    const sTo = submitNoTo.trim();
    if (sFrom && sTo && sTo < sFrom) {
      message.warning('送件單號範圍不正確，結束值不可小於起始值');
      return;
    }

    if (testDateFrom && testDateTo && testDateTo < testDateFrom) {
      message.warning('測試完成日範圍不正確，結束日期不可早於起始日期');
      return;
    }

    const rFrom = reportNoFrom.trim();
    const rTo = reportNoTo.trim();
    if (rFrom && rTo && rTo < rFrom) {
      message.warning('報告單號範圍不正確，結束值不可小於起始值');
      return;
    }

    const req: QueryRequest = {
      deliveryNoFrom: dFrom,
      deliveryNoTo: dTo,
      deliverySeqFrom,
      deliverySeqTo,
      submitNoFrom: sFrom,
      submitNoTo: sTo,
      testDateFrom,
      testDateTo,
      reportNoFrom: rFrom,
      reportNoTo: rTo,
    };
    runQuery(req);
  };

  return (
    <Spin spinning={loading}>
      <div className="pwp303-container max-w-7xl mx-auto p-4">
        <h2 className="text-xl font-bold mb-4">
          物化性報告明細表 (P3031)
        </h2>

        <QueryParams
          deliveryNoFrom={deliveryNoFrom}
          deliveryNoTo={deliveryNoTo}
          deliverySeqFrom={deliverySeqFrom}
          deliverySeqTo={deliverySeqTo}
          submitNoFrom={submitNoFrom}
          submitNoTo={submitNoTo}
          testDateFrom={testDateFrom}
          testDateTo={testDateTo}
          reportNoFrom={reportNoFrom}
          reportNoTo={reportNoTo}
          loading={loading}
          onDeliveryNoFromChange={setDeliveryNoFrom}
          onDeliveryNoToChange={setDeliveryNoTo}
          onDeliverySeqFromChange={setDeliverySeqFrom}
          onDeliverySeqToChange={setDeliverySeqTo}
          onSubmitNoFromChange={setSubmitNoFrom}
          onSubmitNoToChange={setSubmitNoTo}
          onTestDateFromChange={setTestDateFrom}
          onTestDateToChange={setTestDateTo}
          onReportNoFromChange={setReportNoFrom}
          onReportNoToChange={setReportNoTo}
          onQuery={handleQuery}
        />

        {queried && !noData && result && (
          <ResultTable result={result} />
        )}

        {queried && noData && (
          <Alert
            message="查無資料"
            description="查無符合條件的物化性報告！請確認篩選條件是否正確。"
            type="info"
            showIcon
          />
        )}
      </div>
    </Spin>
  );
};

export default PWP303Form;
